import { Type, Static} from '@sinclair/typebox'
import { FormatRegistry } from '@sinclair/typebox'

FormatRegistry.Set('uri', (value) => {
  // workaround for locally hosted images
  if (value.startsWith('/images')) return true
  return /^(?:[a-z][a-z0-9+\-.]*:)(?:\/?\/)?[^\s]*$/i.test(value)
})

import { PermissionRoleTB } from './permission'
import { SubscriptionTB } from './subscription'
import { PropertyWithReportSummariesTB } from './property'
import { IntegrationAgriwebbTB } from './integrations'
import { VisionOrgPreferencesTB, VisionOrgThemeTB } from './vision-organisation'

export const OrganisationTypeFarmTB = Type.Literal('farm')
export const OrganisationTypeVisionTB = Type.Literal('vision')
export const OrganisationTypeRecordsTB = Type.Literal('records')
export const OrganisationTypeRuminatiTB = Type.Literal('ruminati')

export const OrganisationTypeTB = Type.Union([
  OrganisationTypeFarmTB,
  OrganisationTypeVisionTB,
  OrganisationTypeRecordsTB,
  OrganisationTypeRuminatiTB
])
export type OrganisationTypeT = Static<typeof OrganisationTypeTB>

export const OrganisationMetadataTB = Type.Object({
  description: Type.Optional(Type.String({maxLength: 200})),
  abn: Type.Optional(Type.String()),
  report_start_month: Type.Optional(Type.Number({minimum: 1, maximum: 12}))
})
export type OrganisationMetadataT = Static<typeof OrganisationMetadataTB>

export const OrganisationPreferencesTB = Type.Object({
  emissionsOrg: Type.Optional(Type.Object({
    betaFeatures: Type.Optional(Type.Object({
      sbGafImport: Type.Optional(Type.Boolean()),
      agworldImport: Type.Optional(Type.Boolean()),
      evidence: Type.Optional(Type.Boolean())
    }))
  })),
  partnerOrg: Type.Optional(Type.Object({
    hideInDirectory: Type.Optional(Type.Boolean()),
    betaFeatures: Type.Optional(Type.Object({
      sbGafImport: Type.Optional(Type.Boolean()),
      agworldImport: Type.Optional(Type.Boolean()),
      evidence: Type.Optional(Type.Boolean())
    }))
  })),
  visionOrg: Type.Optional(VisionOrgPreferencesTB),
  visionOrgTheme: Type.Optional(VisionOrgThemeTB)
})
export type OrganisationPreferencesT = Static<typeof OrganisationPreferencesTB>

export const OrganisationTB = Type.Object({
  id: Type.String({
    description: "Ruminati Organisation ID"
  }),
  name: Type.String({
    description: "The Organisation Name as specified by the user",
    minLength: 1,
  }),
  type: OrganisationTypeTB,
  iconSmallUrl: Type.Optional(
    Type.Union([
      Type.String({
        format: 'uri'
      }),
      Type.Null()
    ])
  ), // N.B. entity def allows for null, but optional rather than nullable in application code
  logoUrl: Type.Optional(Type.Union([Type.String(), Type.Null()])), // N.B. entity def allows for null, but optional rather than nullable in application code
  subscription: SubscriptionTB,
  auctionsPlusStatusRetrievalApproved: Type.Boolean(),
  createdDate: Type.Date(),
  deletedAt: Type.Optional(Type.Union([Type.String(), Type.Null()])), // N.B. entity def allows for null, but optional rather than nullable in application code
  metadata: Type.Optional(OrganisationMetadataTB),
  preferences: Type.Optional(OrganisationPreferencesTB)
})
export type OrganisationT = Static<typeof OrganisationTB>

// type cannot be specified when creating
// N.B. change that if we ever allow programmatic creation of partner / vision orgs
export const OrganisationCreateTB = Type.Omit(OrganisationTB, ['id', 'createdDate', 'type', 'subscription', 'auctionsPlusStatusRetrievalApproved'])
export type OrganisationCreateT = Static<typeof OrganisationCreateTB>

// type cannot be updated
// TODO: should subscription be able to be updated? or is this updated separately?
export const OrganisationUpdateTB = Type.Partial(Type.Omit(OrganisationTB, ['id', 'createdDate', 'deletedAt', 'type', 'subscription']))
export type OrganisationUpdateT = Static<typeof OrganisationUpdateTB>

// N.B. used for e2e tests
export const OrganisationCreateWithTypeTB = Type.Omit(OrganisationTB, ['id', 'createdDate', 'subscription', 'auctionsPlusStatusRetrievalApproved'])
export type OrganisationCreateWithTypeT = Static<typeof OrganisationCreateWithTypeTB>

export const PartnerOrganisationUpdateFormTB = Type.Composite([
  Type.Pick(OrganisationTB, ['name', 'iconSmallUrl', 'logoUrl']),
  Type.Partial(Type.Object({
    listingDescription: Type.Optional(Type.String()),
    hideInDirectory: Type.Boolean()
  }))
])
export type PartnerOrganisationUpdateFormT = Static<typeof PartnerOrganisationUpdateFormTB>

export const OrganisationWithUserRoleTB = Type.Composite([OrganisationTB, Type.Object({ role: PermissionRoleTB })])
export type OrganisationWithUserRoleT = Static<typeof OrganisationWithUserRoleTB>

export const OrganisationSummaryTB = Type.Pick(OrganisationTB, [
  'id',
  'name',
  'subscription',
])
export type OrganisationSummaryT = Static<typeof OrganisationSummaryTB>

export const OrganisationWithPropertiesTB = Type.Composite([
  OrganisationTB,
  Type.Object({
    properties: Type.Array(PropertyWithReportSummariesTB),
    primaryContact: Type.String(),
    staffAssigned: Type.Array(
      Type.Object({
        name: Type.String()
      })
    )
  })
])
export type OrganisationWithPropertiesT = Static<typeof OrganisationWithPropertiesTB>

// adds "active" to mark whether partner is active for current org
export const OrganisationPartnerTB = Type.Composite([
  OrganisationTB,
  Type.Object({
    active: Type.Optional(Type.Boolean())
  })
])
export type OrganisationPartnerT = Static<typeof OrganisationPartnerTB>

// adds "active" to mark whether partner is active for current org
export const OrganisationCorporateTB = Type.Composite([
  OrganisationTB,
  Type.Object({
    active: Type.Optional(Type.Boolean())
  })
])
export type OrganisationCorporateT = Static<typeof OrganisationCorporateTB>

// a Partner Org user creating a Client
// N.B. they don't create a password - the user will receive an email with a link to complete their setup, and they may choose to register with credentials (i.e. provide a password), or register with an OAuth provider (in which case, we use the OAuth details which may have a different email, names etc).
export const OrganisationCreateClientTB = Type.Composite([
  OrganisationCreateTB,
  // TODO: should be the picked type, but creates circular dependencies
  // Type.Pick(RegisterAccountTB, ["email", "firstName", "lastName"])
  Type.Object({
    email: Type.String(),
    firstName: Type.String(),
    lastName: Type.String(),
  }),
], {
  // defaults for create client form
  default: {
    name: undefined,
    email: undefined,
    firstName: undefined,
    lastName: undefined
  }
})
export type OrganisationCreateClientT = Static<typeof OrganisationCreateClientTB>

export const OrganisationWithIntegrationsTB = Type.Composite([
  OrganisationTB,
  Type.Object({
    // add more integrations as we develop them
    // TODO: bring AuctionsPlus into line with this approach?
    integrationAgriwebb: Type.Optional(Type.Union([
      Type.Literal('invalid'),
      IntegrationAgriwebbTB
    ])),
  })
])
export type OrganisationWithIntegrationsT = Static<typeof OrganisationWithIntegrationsTB>
